import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { InlineNotification } from '~/components/InlineNotification';
import { useGlobal } from '~/hooks/useGlobal';
import { notificationService } from '~/services/NotificationService';

import styles from './NotificationContainer.module.scss';

const NotificationItem: React.FC<{ id: string }> = observer(({ id }) => {
  const item = notificationService.notifications.find((n) => n.id === id);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!item) return;

    timerRef.current = setTimeout(() => {
      notificationService.remove(id);
    }, item.duration);

    return () => clearTimeout(timerRef.current);
  }, [id, item]);

  if (!item) return null;

  return (
    <InlineNotification
      variant={item.variant}
      title={item.title}
      description={item.description}
      onClose={() => notificationService.remove(id)}
      className={styles.notification}
    />
  );
});

const NotificationContainer = observer(() => {
  const { isSSR } = useGlobal();

  if (isSSR || !notificationService.notifications.length) {
    return null;
  }

  return createPortal(
    <div className={styles.container}>
      {notificationService.notifications.map((n) => (
        <NotificationItem key={n.id} id={n.id} />
      ))}
    </div>,
    document.body,
  );
});

export default NotificationContainer;
